// src/pages/Management.tsx
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../redux/store";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, LogOut, User, Eye, EyeOff, AlertCircle, Loader,
} from "lucide-react";
import { loginWithEmail, logout } from "../redux/authSlice";
import ECareer from "../EDIT/ECareer";
import ECompany from "../EDIT/ECompany";
import Editorial from "./EDITORIAL/Editorial";



/* ─── Font ─── */
const fontHref = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

/* ─── TOKENS ─── */
const C = {
  white:   "#FFFFFF",
  offWhite:"#F8F7F5",
  black:   "#0A0A0A",
  dark:    "#111111",
  red:     "#E50914",
  redDark: "#B00710",
  redLt:   "#FFF0F0",
  gray:    "#6B6B6B",
  grayLt:  "#D4D4D4",
  border:  "#E5E3DF",
  gold:    "#D4AF37",
};

/* ════════════════════════════════
   MAIN
   ════════════════════════════════ */
const Management: React.FC = () => {
  const { user, authLoaded } = useSelector((s: RootState) => s.auth);
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  /* ── Loading ── */
  if (!authLoaded) return <LoadingScreen />;

  /* ── Not logged in ── */
  if (!user) return <LoginScreen />;

  /* ── Access check ── */
  const isAuthorized =
    user.isAdmin     === true &&
    user.isExecutive === true &&
    user.is7         === true &&
  user.isDirector === true;

  if (!isAuthorized) return <AccessDenied />;

  /* ── Dashboard ── */
  return (
    <div style={{ background: C.offWhite, minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── NAV ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: C.white, borderBottom: `1px solid ${C.border}`,
        height: 64, padding: "0 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 32, height: 32, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Serif Display', serif", fontSize: 13, color: C.white, letterSpacing: 1 }}>
            SG
          </div>
          <div>
            <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 16, letterSpacing: "0.15em", color: C.black, lineHeight: 1 }}>4REVA GROUP</div>
            <div style={{ fontSize: 9, letterSpacing: "0.3em", color: C.gray, textTransform: "uppercase", marginTop: 2 }}>Operations</div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ fontSize: 12, color: C.gray }}>
            {user.firstName} {user.lastName}
          </div>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e" }} title="Active" />
          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => setMenuOpen(true)}
            style={{ background: "none", border: `1px solid ${C.border}`, padding: "8px", cursor: "pointer", display: "flex", borderRadius: 0 }}>
            <Menu size={18} color={C.black} />
          </motion.button>
        </div>
      </nav>

      {/* ── CONTENT ── */}
      <div style={{ paddingTop: 64 }}>
  <ECareer />
  <ECompany />
        <Editorial />

      </div>

      {/* ── SIDE MENU ── */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, backdropFilter: "blur(4px)" }} />

            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{
                position: "fixed", top: 0, right: 0, bottom: 0,
                width: 300, background: C.white, zIndex: 300,
                borderLeft: `1px solid ${C.border}`,
                display: "flex", flexDirection: "column",
                padding: "32px 28px",
              }}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 40 }}>
                <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: C.black, letterSpacing: "0.1em" }}>Menu</span>
                <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                  <X size={20} color={C.gray} />
                </button>
              </div>

              {/* User card */}
              <div style={{ background: C.offWhite, border: `1px solid ${C.border}`, padding: "20px", marginBottom: 32, borderLeft: `3px solid ${C.red}` }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.black, marginBottom: 4 }}>
                  {user.firstName} {user.lastName}
                </div>
                <div style={{ fontSize: 11, color: C.gray, marginBottom: 8 }}>{user.email}</div>
                <div style={{ display: "inline-block", background: C.red, padding: "3px 10px", fontSize: 9, fontWeight: 700, color: C.white, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                  CEO
                </div>
              </div>

              {/* Nav items */}
              <div style={{ flex: 1 }}>
                <MenuLink icon={<User size={16} />} label="Profile" onClick={() => { navigate(`/profile/${user.userId}`); setMenuOpen(false); }} />
              </div>

              {/* Logout */}
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                onClick={async () => { await dispatch(logout()); setMenuOpen(false); }}
                style={{
                  width: "100%", background: C.black, color: C.white, border: "none",
                  padding: "14px 0", fontWeight: 700, fontSize: 12, letterSpacing: "0.16em",
                  cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                }}>
                <LogOut size={15} />
                SIGN OUT
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Management;

/* ════════════════════════════════
   LOGIN SCREEN
   ════════════════════════════════ */
function LoginScreen() {
  const dispatch = useDispatch<AppDispatch>();
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [focus,    setFocus]    = useState<string | null>(null);

  const inp = (field: string): React.CSSProperties => ({
    width: "100%", boxSizing: "border-box" as const,
    background: "#F8F7F5",
    border: `1.5px solid ${focus === field ? C.red : C.border}`,
    padding: "13px 16px",
    color: C.black, fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none", transition: "border-color 0.2s",
  });

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true);
    setError(null);
    try {
      if (email.includes("@")) {
        await (dispatch as AppDispatch)(loginWithEmail({ email, password })).unwrap();
      } else {
        const res  = await fetch(`/api/findUserByUsername?username=${email}`);
        const data = await res.json();
        if (!data?.email) throw new Error("Username not found");
        await (dispatch as AppDispatch)(loginWithEmail({ email: data.email, password })).unwrap();
      }
    } catch (err: any) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.offWhite, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        style={{ background: C.white, width: "100%", maxWidth: 420, borderTop: `3px solid ${C.red}` }}>

        {/* Header */}
        <div style={{ padding: "36px 40px 28px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
            <div style={{ width: 32, height: 32, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Serif Display', serif", fontSize: 13, color: C.white }}>SG</div>
            <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 16, letterSpacing: "0.15em", color: C.black }}>4REVA GROUP</span>
          </div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: C.black, margin: "0 0 6px" }}>
            Operations Access
          </h1>
          <p style={{ fontSize: 13, color: C.gray, margin: 0, lineHeight: 1.6 }}>
            Restricted to authorised executives only.
          </p>
        </div>

        {/* Form */}
        <div style={{ padding: "28px 40px 36px" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: "#444", textTransform: "uppercase", marginBottom: 8 }}>
              Email or Username
            </label>
            <input value={email} onChange={e => setEmail(e.target.value)}
              onFocus={() => setFocus("email")} onBlur={() => setFocus(null)}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              placeholder="you@4reva.com" style={inp("email")} />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: "#444", textTransform: "uppercase", marginBottom: 8 }}>
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocus("pw")} onBlur={() => setFocus(null)}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                placeholder="••••••••" style={{ ...inp("pw"), paddingRight: 44 }} />
              <button onClick={() => setShowPw(s => !s)}
                style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
                {showPw ? <EyeOff size={16} color={C.gray} /> : <Eye size={16} color={C.gray} />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: C.redLt, border: `1px solid ${C.red}20`, color: C.red, fontSize: 13, marginBottom: 20 }}>
                <AlertCircle size={15} />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button whileHover={{ scale: loading ? 1 : 1.01 }} whileTap={{ scale: loading ? 1 : 0.98 }}
            onClick={handleLogin} disabled={loading}
            style={{
              width: "100%", background: loading ? C.grayLt : C.red, color: loading ? C.gray : C.white,
              border: "none", padding: "15px 0", fontWeight: 700, fontSize: 13, letterSpacing: "0.16em",
              cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10, transition: "all 0.2s",
            }}>
            {loading ? <><Loader size={15} className="spin" /> AUTHENTICATING…</> : "ACCESS DASHBOARD"}
          </motion.button>
        </div>
      </motion.div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

/* ════════════════════════════════
   ACCESS DENIED
   ════════════════════════════════ */
function AccessDenied() {
  return (
    <div style={{ minHeight: "100vh", background: C.black, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
        style={{ textAlign: "center", padding: "60px 40px" }}>
        <div style={{ width: 64, height: 64, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", borderRadius: "50%" }}>
          <AlertCircle size={32} color={C.white} />
        </div>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, fontWeight: 400, color: C.white, margin: "0 0 12px" }}>
          Access Denied
        </h2>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", margin: 0, lineHeight: 1.7, maxWidth: 340 }}>
          You do not have the required authorisation level to access this dashboard.
        </p>
      </motion.div>
    </div>
  );
}

/* ════════════════════════════════
   LOADING
   ════════════════════════════════ */
function LoadingScreen() {
  return (
    <div style={{ minHeight: "100vh", background: C.black, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ width: 36, height: 36, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Serif Display', serif", fontSize: 14, color: C.white }}>SG</div>
      <Loader size={20} color={C.red} className="spin" />
      <p style={{ fontSize: 12, letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase", margin: 0 }}>Verifying access</p>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}

/* ─── Menu link ─── */
function MenuLink({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: `1px solid ${C.border}`, cursor: "pointer", color: hov ? C.red : C.black, transition: "color 0.2s" }}>
      {icon}
      <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
    </div>
  );
}